import { useMemo } from 'react';
import EcHierarchyTree from './EcHierarchyTree';
import LevelHierarchyEditor, { type Level } from './LevelHierarchyEditor';
import type { HierarchyView, NodeKind } from './DefineBoundaryStep';

// Re-exported for callers that still import the level type from this step.
export type { Level };

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col rounded-xl border border-line-soft bg-white p-6 shadow-card">
      {children}
    </div>
  );
}

/** A node in the condensed constituency chain (forks at the Rural/Urban branch). */
interface ChainNode {
  key: string;
  text: string;
  /** true = an actual selected place (State / PC); false = a generic level name. */
  concrete: boolean;
  /** true = this is the exact level the user checked in step 2. */
  selected: boolean;
  children: ChainNode[];
}

/** Nest a flat list so each item becomes the child of the one before it. Returns the head. */
function nestLinear(list: ChainNode[]): ChainNode[] {
  for (let i = list.length - 1; i > 0; i -= 1) list[i - 1].children = [list[i]];
  return list.length ? [list[0]] : [];
}

/**
 * Condensed constituency summary: the actual State & Parliament the user picked
 * (e.g. Rajasthan → Alwar), then just the level *names* below. The Rural / Urban
 * branch forks so both settlement paths can be navigated:
 *   Assembly Constituency → ┬ Rural → Gram Panchayat → Village → Booth
 *                           └ Urban → Urban Local Body → Ward → Village → Booth
 */
export function SelectedHierarchy({ nodes }: { nodes: HierarchyView[] }) {
  const roots = useMemo<ChainNode[]>(() => {
    const flat: HierarchyView[] = [];
    const walk = (n: HierarchyView) => {
      flat.push(n);
      n.children.forEach(walk);
    };
    nodes.forEach(walk);

    // Distinct labels present per level kind (root ignored).
    const byKind = new Map<NodeKind, string[]>();
    for (const n of flat) {
      if (!n.kind || n.kind === 'root') continue;
      const arr = byKind.get(n.kind) ?? [];
      if (!arr.includes(n.label)) arr.push(n.label);
      byKind.set(n.kind, arr);
    }
    const has = (k: NodeKind) => byKind.has(k);
    // Nothing selected yet → no chain.
    if (byKind.size === 0) return [];

    // Labels the user actually checked, per kind — used to flag the selected level.
    const selectedByKind = new Map<NodeKind, Set<string>>();
    for (const n of flat) {
      if (!n.kind || n.kind === 'root' || !n.selected) continue;
      const set = selectedByKind.get(n.kind) ?? new Set<string>();
      set.add(n.label);
      selectedByKind.set(n.kind, set);
    }
    const selKind = (k: NodeKind) => selectedByKind.has(k);
    const selLabel = (k: NodeKind, label: string) => selectedByKind.get(k)?.has(label) ?? false;
    const selBranch = (re: RegExp) =>
      Array.from(selectedByKind.get('branch') ?? new Set<string>()).some((l) => re.test(l));

    const generic = (key: string, text: string, selected = false): ChainNode => ({
      key,
      text,
      concrete: false,
      selected,
      children: [],
    });

    // Once a branch is selected, show its full downward level chain — even the levels
    // below the deepest picked node (e.g. picking a Gram Panchayat still shows
    // Village → Booth beneath it), so the structure under the selection is visible.
    const ruralTail = nestLinear([
      generic('gram', 'Gram Panchayat', selKind('gram')),
      generic('r-village', 'Village', selKind('village')),
      generic('r-booth', 'Booth', selKind('booth')),
    ]);
    const urbanTail = nestLinear([
      generic('ulb', 'Urban Local Body', selKind('ulb')),
      generic('ward', 'Ward', selKind('ward')),
      generic('u-village', 'Village', selKind('village')),
      generic('u-booth', 'Booth', selKind('booth')),
    ]);

    // Which settlement branch the user actually entered:
    //   • went Rural (a gram, or the Rural branch)  → show only Rural
    //   • went Urban (a ULB/ward, or Urban branch)  → show only Urban
    //   • stopped at the Assembly (no branch chosen) → show both Rural & Urban
    const branchLabels = byKind.get('branch') ?? [];
    const ruralSel = has('gram') || branchLabels.some((l) => /rural/i.test(l));
    const urbanSel = has('ulb') || has('ward') || branchLabels.some((l) => /urban/i.test(l));
    const bothWhenAmbiguous = !ruralSel && !urbanSel; // no specific branch chosen → both
    const branchNodes: ChainNode[] = [];
    if (ruralSel || bothWhenAmbiguous)
      branchNodes.push({
        key: 'rural',
        text: 'Rural',
        concrete: false,
        selected: selBranch(/rural/i),
        children: ruralTail,
      });
    if (urbanSel || bothWhenAmbiguous)
      branchNodes.push({
        key: 'urban',
        text: 'Urban',
        concrete: false,
        selected: selBranch(/urban/i),
        children: urbanTail,
      });

    // Below a Parliament Constituency the structure always continues down to Booth,
    // so selecting just a PC still reveals Assembly Constituency → … → Booth.
    const belowPc: ChainNode[] = [
      {
        key: 'assembly',
        text: 'Assembly Constituency',
        concrete: false,
        selected: selKind('assembly'),
        children: branchNodes,
      },
    ];

    // Parliament → Assembly subtree; State → Parliament (generic when no PC picked).
    const pcNodes: ChainNode[] = (byKind.get('parliament') ?? []).map((name, i) => ({
      key: `pc-${i}`,
      text: name,
      concrete: true,
      selected: selLabel('parliament', name),
      children: belowPc,
    }));
    const genericPc: ChainNode = {
      key: 'pc-generic',
      text: 'Parliament Constituency',
      concrete: false,
      selected: false,
      children: belowPc,
    };
    const belowState = pcNodes.length ? pcNodes : [genericPc];
    const stateNodes: ChainNode[] = (byKind.get('state') ?? []).map((name, i) => ({
      key: `state-${i}`,
      text: name,
      concrete: true,
      selected: selLabel('state', name),
      children: belowState,
    }));

    return stateNodes.length ? stateNodes : belowState;
  }, [nodes]);

  if (roots.length === 0) {
    return <p className="px-3 py-2 text-sm text-ink-secondary">No constituency selected yet.</p>;
  }

  const renderNode = (n: ChainNode): React.ReactNode => {
    const emphasized = n.concrete || n.selected;
    return (
      <div key={n.key}>
        <div
          className={[
            'flex items-center gap-2.5 px-3 py-2',
            n.selected ? 'rounded-lg border border-[#bfdbfe] bg-[#eff6ff]' : '',
          ].join(' ')}
        >
          <span className="flex size-5 shrink-0 items-center justify-center">
            <span
              className={['size-1.5 rounded-full', emphasized ? 'bg-brand' : 'bg-[#94a3b8]'].join(' ')}
            />
          </span>
          <span
            className={[
              'text-sm',
              n.selected
                ? 'font-semibold text-brand'
                : n.concrete
                  ? 'font-semibold text-ink'
                  : 'font-medium text-ink-secondary',
            ].join(' ')}
          >
            {n.text}
          </span>
          {n.selected ? (
            <span className="ml-auto flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-[#10b981]" />
              <span className="text-[10px] font-bold tracking-[0.5px] text-[#059669]">SELECTED</span>
            </span>
          ) : (
            !n.concrete && (
              <span className="ml-auto rounded bg-[#f1f5f9] px-2 py-1 text-[10px] font-bold tracking-[0.25px] text-[#64748b]">
                LEVEL
              </span>
            )
          )}
        </div>
        {n.children.length > 0 && (
          <div className="ml-[19px] flex flex-col border-l border-dashed border-[#cbd5e1] pl-3">
            {n.children.map(renderNode)}
          </div>
        )}
      </div>
    );
  };

  return <div className="flex flex-col">{roots.map(renderNode)}</div>;
}

/** Step 3 — sequence the organization's administrative levels against the EC hierarchy. */
export default function BuildHierarchyStep({
  selection,
  onLevelsChange,
  onLevelsDataChange,
  initialLevels,
}: {
  selection?: HierarchyView[];
  onLevelsChange?: (levels: string[]) => void;
  onLevelsDataChange?: (levels: Level[]) => void;
  initialLevels?: Level[];
}) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* Left — selection from step 2 (falls back to the EC reference tree) */}
      <Card>
        {selection && selection.length > 0 ? (
          <>
            <h2 className="text-lg font-bold leading-6 text-ink">Selected Constituency Hierarchy</h2>
            <div className="mt-6">
              <SelectedHierarchy nodes={selection} />
            </div>
          </>
        ) : (
          <>
            <h2 className="text-lg font-bold leading-6 text-ink">
              Election Commission Level Hierarchy
            </h2>
            <div className="mt-6">
              <EcHierarchyTree rootLabel="Alwar Assembly Constituency" />
            </div>
          </>
        )}
      </Card>

      {/* Right — editable organization levels (shared add/display logic) */}
      <LevelHierarchyEditor
        title="Party Level Hierarchy"
        initialLevels={initialLevels}
        onLevelsChange={onLevelsChange}
        onLevelsDataChange={onLevelsDataChange}
      />
    </div>
  );
}
